import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Button, Card, EmptyState, SectionHeader } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createInsightsApi, type InsightRange } from "../insights/api";
import {
  BarChart,
  CHART_COLORS,
  Delta,
  EnvBanner,
  HBars,
  Kpi,
  KpiGrid,
  Legend,
  Note,
  Sparkline,
  TwoCol,
} from "../insights/charts";
import {
  formatAgo,
  formatLabel,
  formatMoneyList,
  formatNumber,
  sumBy,
} from "../insights/format";
import { InsightBoundary, RangeSelect } from "../insights/parts";
import type { DashboardActivityItem, DashboardDto } from "../insights/types";
import { useInsight } from "../insights/useInsight";
import styles from "../insights/insights.module.css";

const ACTIVITY_LINKS: Record<string, ((id: string) => string) | undefined> = {
  booking: (id) => `/app/bookings/${id}`,
  business: (id) => `/app/businesses/${id}`,
  user: (id) => `/app/users/${id}`,
  support_ticket: (id) => `/app/support/tickets/${id}`,
  community: (id) => `/app/communities/${id}`,
  report: (id) => `/app/trust-safety/reports/${id}`,
  moderation_case: (id) => `/app/trust-safety/cases/${id}`,
  approval: (id) => `/app/approvals/${id}`,
  task: (id) => `/app/tasks/${id}`,
  bookable_resource: (id) => `/app/businesses/resources/${id}`,
};

function activityTitle(item: DashboardActivityItem): string {
  if (item.kind === "signup") return `${item.actor ?? "A new member"} joined HUDDL Play`;
  const what = formatLabel(item.action);
  return item.actor ? `${item.actor}: ${what}` : what;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createInsightsApi(client), [client]);
  const [days, setDays] = useState<InsightRange>(30);

  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Dashboard" }]);

  const state = useInsight(() => api.dashboard(days), [api, days]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Dashboard"
        description="What is happening on HUDDL right now, and what needs a person. Every number is counted from live records."
        actions={
          <Flex gap={12} align="flex-end" wrap>
            <RangeSelect value={days} onChange={setDays} />
            <Button variant="secondary" onClick={state.reload} loading={state.loading}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <InsightBoundary state={state} what="the dashboard">
        {(d) => <DashboardBody d={d} days={days} go={navigate} />}
      </InsightBoundary>
    </Stack>
  );
}

function DashboardBody({ d, days, go }: { d: DashboardDto; days: InsightRange; go: (to: string) => void }) {
  const acc = d.access;
  const q = d.queues;
  const rangeLabel = `previous ${days} days`;

  const empty =
    d.players?.total === 0 && (d.bookings?.total ?? 0) === 0 && (d.businesses?.total ?? 0) === 0;

  const attention: { key: string; label: string; value: number | null; to: string; meta: string }[] = [
    { key: "tickets", label: "Open support tickets", value: q.open_tickets, to: "/app/support", meta: q.new_tickets ? `${q.new_tickets} new` : "None new" },
    { key: "sla", label: "Tickets past SLA", value: q.sla_breached, to: "/app/support", meta: "Response overdue" },
    { key: "reports", label: "Open reports", value: q.open_reports, to: "/app/trust-safety/reports", meta: "Awaiting triage or action" },
    { key: "cases", label: "Open moderation cases", value: q.open_cases, to: "/app/trust-safety/cases", meta: q.critical_cases ? `${q.critical_cases} high or critical` : "None high severity" },
    { key: "approvals", label: "Pending approvals", value: q.pending_approvals, to: "/app/approvals/queue", meta: "Decisions waiting on staff" },
    { key: "bizapps", label: "Business applications", value: q.business_applications, to: "/app/businesses", meta: "Applied, in review or awaiting information" },
    { key: "verify", label: "Verification queue", value: q.verification_records, to: "/app/businesses", meta: "Records to review" },
    { key: "creators", label: "Creator applications", value: q.creator_applications, to: "/app/creators/applications", meta: "Submitted or in review" },
  ];

  return (
    <Stack gap={24}>
      <EnvBanner
        kind={d.environment.kind}
        label={d.environment.label}
        projectRef={d.environment.supabase_project_ref}
      />

      {empty ? (
        <Card>
          <EmptyState
            title="No activity has been recorded yet"
            description="Numbers appear here as real people sign up in HUDDL Play, businesses register and players book. There is nothing to configure on this page."
            reason="none"
          />
        </Card>
      ) : null}

      <Stack gap={12}>
        <Text role="title">Needs attention</Text>
        <KpiGrid>
          {attention.map((a) => (
            <Kpi
              key={a.key}
              label={a.label}
              value={formatNumber(a.value ?? 0)}
              meta={a.meta}
              denied={a.value === null}
              attention={(a.value ?? 0) > 0}
              onClick={() => go(a.to)}
            />
          ))}
          <Kpi
            label="Failed integration events"
            value={formatNumber(d.system?.integration_failed ?? 0)}
            meta={`${formatNumber(d.system?.domain_events_failed ?? 0)} failed domain events`}
            denied={d.system === null}
            attention={(d.system?.integration_failed ?? 0) + (d.system?.domain_events_failed ?? 0) > 0}
            onClick={() => go("/app/system")}
          />
          <Kpi
            label="Media deletion queue"
            value={formatNumber(d.system?.media_deletion_queue ?? 0)}
            meta="Files waiting to be removed from storage"
            denied={d.system === null}
            attention={(d.system?.media_deletion_queue ?? 0) > 25}
            onClick={() => go("/app/system")}
          />
        </KpiGrid>
      </Stack>

      <Stack gap={12}>
        <Text role="title">Growth and activity</Text>
        <KpiGrid>
          <Kpi
            label="Players"
            denied={!acc.players || d.players === null}
            value={formatNumber(d.players?.total ?? 0)}
            meta={
              d.players ? (
                <Delta current={d.players.new_in_window} previous={d.players.new_prev_window} label={rangeLabel} />
              ) : null
            }
            spark={
              d.signups_series ? (
                <Sparkline values={d.signups_series.map((p) => p.signups)} ariaLabel="Sign-ups per day" />
              ) : null
            }
            onClick={() => go("/app/users")}
          />
          <Kpi
            label={`New sign-ups (${days} days)`}
            denied={d.players === null}
            value={formatNumber(d.players?.new_in_window ?? 0)}
            meta={d.players ? `${formatNumber(d.players.onboarded)} of ${formatNumber(d.players.total)} finished onboarding` : null}
            onClick={() => go("/app/users")}
          />
          <Kpi
            label="Signed in, last 30 days"
            denied={d.players === null}
            value={formatNumber(d.players?.signed_in_30d ?? 0)}
            meta={d.players ? `${formatNumber(d.players.signed_in_7d)} in the last 7 days` : null}
          />
          <Kpi
            label="Active communities"
            denied={d.communities === null}
            value={formatNumber(d.communities?.active ?? 0)}
            meta={
              d.communities
                ? `${formatNumber(d.communities.members)} active members · ${formatNumber(d.communities.pending_join_requests)} join requests waiting`
                : null
            }
            onClick={() => go("/app/communities")}
          />
          <Kpi
            label={`Bookings created (${days} days)`}
            denied={d.bookings === null}
            value={formatNumber(d.bookings?.in_window ?? 0)}
            meta={
              d.bookings ? (
                <Delta current={d.bookings.in_window} previous={d.bookings.prev_window} label={rangeLabel} />
              ) : null
            }
            spark={
              d.bookings_series ? (
                <Sparkline
                  values={d.bookings_series.map((p) => p.created)}
                  color={CHART_COLORS.accent}
                  ariaLabel="Bookings created per day"
                />
              ) : null
            }
            onClick={() => go("/app/bookings")}
          />
          <Kpi
            label="Upcoming bookings"
            denied={d.bookings === null}
            value={formatNumber(d.bookings?.upcoming ?? 0)}
            meta="Confirmed or awaiting payment, in the future"
            onClick={() => go("/app/bookings")}
          />
          <Kpi
            label="Booked value"
            denied={d.bookings === null}
            small
            value={formatMoneyList(d.bookings?.booked_value ?? [], "None yet")}
            meta={
              d.bookings
                ? `${formatMoneyList(d.bookings.booked_value_window, "None")} created in the last ${days} days. Price of confirmed, in-progress and completed bookings — not money collected.`
                : null
            }
          />
          <Kpi
            label="Payments collected"
            denied={!acc.finance || d.finance === null}
            small
            value={formatMoneyList(d.finance?.collected ?? [], "None yet")}
            meta={
              d.finance
                ? d.finance.transactions_total === 0
                  ? "No payment has been recorded in the ledger yet."
                  : `${formatNumber(d.finance.refunds_open)} refunds and ${formatNumber(d.finance.payouts_pending)} payouts need attention`
                : null
            }
            onClick={() => go("/app/finance")}
          />
          <Kpi
            label="Businesses"
            denied={d.businesses === null}
            value={formatNumber(d.businesses?.total ?? 0)}
            meta={
              d.businesses
                ? `${formatNumber(d.businesses.by_status.active ?? 0)} active · ${formatNumber(d.businesses.listed_offers)} listings on sale`
                : null
            }
            onClick={() => go("/app/businesses")}
          />
        </KpiGrid>
      </Stack>

      <TwoCol>
        <Card header={<Text role="title">New sign-ups</Text>}>
          {d.signups_series ? (
            <Stack gap={8}>
              <BarChart
                labels={d.signups_series.map((p) => p.date)}
                series={[
                  {
                    key: "signups",
                    label: "Sign-ups",
                    color: CHART_COLORS.primary,
                    values: d.signups_series.map((p) => p.signups),
                  },
                ]}
                ariaLabel={`New sign-ups per day over the last ${days} days`}
                emptyText="No sign-ups in this range"
              />
              <Note>
                {formatNumber(sumBy(d.signups_series, (p) => p.signups))} in the last {days} days. Counted from the
                account's creation time.
              </Note>
            </Stack>
          ) : (
            <Note>Your role cannot view player figures.</Note>
          )}
        </Card>

        <Card header={<Text role="title">Bookings created and cancelled</Text>}>
          {d.bookings_series ? (
            <Stack gap={8}>
              <BarChart
                labels={d.bookings_series.map((p) => p.date)}
                series={[
                  {
                    key: "created",
                    label: "Created",
                    color: CHART_COLORS.primary,
                    values: d.bookings_series.map((p) => p.created),
                  },
                  {
                    key: "cancelled",
                    label: "Cancelled",
                    color: CHART_COLORS.danger,
                    values: d.bookings_series.map((p) => p.cancelled),
                  },
                ]}
                ariaLabel={`Bookings created and cancelled per day over the last ${days} days`}
                emptyText="No bookings in this range"
              />
              <Legend
                series={[
                  { key: "created", label: "Created", color: CHART_COLORS.primary },
                  { key: "cancelled", label: "Cancelled", color: CHART_COLORS.danger },
                ]}
              />
              <Note>By the day the booking was created, not the day it is played.</Note>
            </Stack>
          ) : (
            <Note>Your role cannot view booking figures.</Note>
          )}
        </Card>
      </TwoCol>

      <TwoCol>
        <Card header={<Text role="title">Bookings by status</Text>}>
          {d.bookings ? (
            <HBars
              empty="No bookings yet. They appear when a player reserves a court in HUDDL Play."
              items={Object.entries(d.bookings.by_status)
                .sort((a, b) => b[1] - a[1])
                .map(([status, n]) => ({ label: formatLabel(status), value: n }))}
            />
          ) : (
            <Note>Your role cannot view booking figures.</Note>
          )}
        </Card>

        <Card header={<Text role="title">Businesses by status</Text>}>
          {d.businesses ? (
            <HBars
              empty="No business has registered yet. Businesses register in HUDDL Play under the Business Manager."
              items={Object.entries(d.businesses.by_status)
                .sort((a, b) => b[1] - a[1])
                .map(([status, n]) => ({ label: formatLabel(status), value: n }))}
            />
          ) : (
            <Note>Your role cannot view business figures.</Note>
          )}
        </Card>
      </TwoCol>

      <TwoCol>
        <Card header={<Text role="title">Content activity ({days} days)</Text>}>
          {d.content ? (
            <HBars
              items={[
                { label: "Chat messages", value: d.content.messages },
                { label: "Community posts", value: d.content.posts },
                { label: "Comments", value: d.content.comments },
                { label: "Reactions (hypes)", value: d.content.hypes },
                { label: "Replays", value: d.content.replays },
              ]}
            />
          ) : (
            <Note>Your role cannot view community figures.</Note>
          )}
        </Card>

        <Card header={<Text role="title">Recent activity</Text>}>
          {d.activity === null ? (
            <Note>Your role cannot view the activity feed.</Note>
          ) : d.activity.length === 0 ? (
            <EmptyState
              title="No recent activity"
              description="Sign-ups, bookings, support tickets and staff actions will be listed here as they happen."
              reason="none"
            />
          ) : (
            <ul className={styles.feed}>
              {d.activity.map((item) => {
                const link = ACTIVITY_LINKS[item.target_type];
                return (
                  <li
                    key={`${item.kind}-${item.target_id}-${item.at}`}
                    className={styles.feedItem}
                    onClick={link ? () => go(link(item.target_id)) : undefined}
                    style={link ? undefined : { cursor: "default" }}
                  >
                    <span className={styles.feedTitle}>{activityTitle(item)}</span>
                    <span className={styles.feedMeta}>{formatAgo(item.at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </TwoCol>

      <Note>
        Figures generated {formatAgo(d.generated_at)} in {d.timezone}. Charts count by local calendar day. Sign-in
        figures come from the account's last sign-in and do not include people who stayed signed in.
      </Note>
    </Stack>
  );
}
